import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, fmtErr } from "../lib/api";
import { industryByKey, PRIORITY_COLORS, daysSince, INDUSTRIES } from "../lib/constants";
import IndustryBadge from "../components/IndustryBadge";
import { FolderOpen, ShieldCheck, Clock, Warning } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";

const CAN_MOVE = ["lead", "project_manager", "admin"];

function SummaryCard({ title, value, byIndustry, icon: Icon, color, testid }) {
  return (
    <div className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-4" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-mono tracking-widest uppercase text-[var(--c-muted)]">{title}</span>
        <Icon size={16} style={{ color }} />
      </div>
      <div className="text-3xl font-medium font-mono text-[var(--c-text)] mt-2" data-testid={`${testid}-value`}>{value}</div>
      <div className="mt-2 space-y-0.5">
        {Object.entries(byIndustry || {}).slice(0, 3).map(([k, v]) => (
          <div key={k} className="flex items-center justify-between text-[11px]">
            <span className="text-[var(--c-muted)] truncate">{industryByKey(k).name}</span>
            <span className="font-mono text-[var(--c-text)]">{v}</span>
          </div>
        ))}
        {Object.keys(byIndustry || {}).length === 0 && <div className="text-[11px] text-[var(--c-muted)]">No projects</div>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [industry, setIndustry] = useState("all");
  const [dragId, setDragId] = useState(null);
  const stages = user?.org?.workflow_stages || [];
  const orgIndustries = user?.org?.industries || [];
  const canMove = CAN_MOVE.includes(user?.role);

  const load = async () => {
    try {
      const [s, p] = await Promise.all([api.get("/dashboard/summary"), api.get("/projects")]);
      setSummary(s.data);
      setProjects(p.data);
    } catch (e) { toast.error(fmtErr(e)); }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(
    () => (industry === "all" ? projects : projects.filter((p) => p.industry === industry)),
    [projects, industry]
  );

  const onDrop = async (stageKey) => {
    const pid = dragId;
    setDragId(null);
    if (!pid) return;
    const proj = projects.find((p) => p.id === pid);
    if (!proj || proj.stage === stageKey) return;
    try {
      await api.patch(`/projects/${pid}/stage`, { stage: stageKey });
      toast.success(`${proj.project_code} moved to ${stages.find((s) => s.key === stageKey)?.name || stageKey}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="p-6" data-testid="dashboard-page">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryCard title="Active Projects" value={summary?.active.total ?? "—"} byIndustry={summary?.active.by_industry} icon={FolderOpen} color="var(--c-accent)" testid="summary-active" />
        <SummaryCard title="In Validation" value={summary?.in_validation.total ?? "—"} byIndustry={summary?.in_validation.by_industry} icon={ShieldCheck} color="var(--c-green)" testid="summary-validation" />
        <SummaryCard title="Awaiting Approval" value={summary?.awaiting_approval.total ?? "—"} byIndustry={summary?.awaiting_approval.by_industry} icon={Clock} color="var(--c-amber)" testid="summary-approval" />
        <SummaryCard title="Overdue" value={summary?.overdue.total ?? "—"} byIndustry={summary?.overdue.by_industry} icon={Warning} color="var(--c-red)" testid="summary-overdue" />
      </div>

      <div className="flex items-center justify-between mt-8 mb-4">
        <h2 className="text-base font-medium text-[var(--c-text)]">Workflow Board</h2>
        <Select value={industry} onValueChange={setIndustry}>
          <SelectTrigger data-testid="industry-filter" className="w-56 h-8 bg-[var(--c-surface)] border-[var(--c-border)] text-[var(--c-text)] text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[var(--c-surface)] border-[var(--c-border)]">
            <SelectItem value="all" className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]">All Industries</SelectItem>
            {orgIndustries.map((k) => (
              <SelectItem key={k} value={k} className="text-[var(--c-text)] focus:bg-[var(--c-surface-2)]" data-testid={`filter-${k}`}>{industryByKey(k).name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex gap-3 overflow-x-auto pb-4 flex-nowrap" data-testid="kanban-board">
        {stages.map((stage) => {
          const cards = filtered.filter((p) => p.stage === stage.key);
          return (
            <div
              key={stage.key}
              data-testid={`kanban-col-${stage.key}`}
              onDragOver={(e) => canMove && e.preventDefault()}
              onDrop={() => canMove && onDrop(stage.key)}
              className="w-64 shrink-0 bg-[var(--c-surface)]/60 border border-[var(--c-border)] rounded-sm flex flex-col max-h-[calc(100vh-300px)]"
            >
              <div className="px-3 py-2.5 border-b border-[var(--c-border)] flex items-center justify-between">
                <span className="text-xs font-medium text-[var(--c-text)]">{stage.name}</span>
                <span className="font-mono text-[10px] text-[var(--c-muted)] bg-[var(--c-bg)] border border-[var(--c-border)] rounded-sm px-1.5 py-0.5">{cards.length}</span>
              </div>
              <div className="p-2 space-y-2 overflow-y-auto flex-1">
                {cards.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`kanban-card-${p.project_code}`}
                    draggable={canMove}
                    onDragStart={() => setDragId(p.id)}
                    onDragEnd={() => setDragId(null)}
                    onClick={() => nav(`/projects/${p.id}`)}
                    className={`bg-[var(--c-surface)] border border-[var(--c-border)] rounded-lg shadow-sm p-3 cursor-pointer hover:border-[var(--c-muted)] transition-colors duration-150 ${
                      dragId === p.id ? "kanban-card-dragging" : ""} ${canMove ? "" : "cursor-pointer"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-mono text-[10px] tracking-wider text-[var(--c-muted)]">{p.project_code}</span>
                      <span className="w-2 h-2 rounded-full" title={`Priority: ${p.priority}`}
                        style={{ backgroundColor: PRIORITY_COLORS[p.priority] || "var(--c-muted)" }} data-testid={`priority-dot-${p.project_code}`} />
                    </div>
                    <div className="text-xs font-medium text-[var(--c-text)] mt-1.5 leading-snug">{p.product_summary}</div>
                    <div className="text-[11px] text-[var(--c-muted)] mt-0.5">{p.client_name}</div>
                    <div className="mt-2"><IndustryBadge industryKey={p.industry} /></div>
                    <div className="flex items-center justify-between mt-2.5 pt-2 border-t border-[var(--c-border)]/60">
                      <span className="text-[10px] text-[var(--c-muted)] truncate">{p.engineer_name}</span>
                      <span className="font-mono text-[10px] text-[var(--c-muted)]">{daysSince(p.stage_entered_at)}d</span>
                    </div>
                  </div>
                ))}
                {cards.length === 0 && <div className="text-[11px] text-[var(--c-muted)] px-1 py-3 text-center">No projects</div>}
              </div>
            </div>
          );
        })}
      </div>
      {!canMove && (
        <div className="text-[11px] text-[var(--c-muted)] mt-1" data-testid="kanban-readonly-note">
          Board is read-only for your role — Leads, Project Managers and Admins can move cards.
        </div>
      )}
    </div>
  );
}
